import { useContext, useEffect, useState } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Grid, Icon, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { checkInfosProduto } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { dropBoolean } from 'utils/drops';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { DropsContext } from 'contexts/DropsContext';
import { AuthContext } from 'contexts/AuthContext';
import { useDialog } from 'components/Modals';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Header from 'components/Header';
import Input from 'components/Input';
import InputMask from 'components/InputMask';
import Loader from 'components/Loader';
import localeText from 'utils/localeText';
import moment from 'moment';

const Rejeição = () => {
  const navigate = useNavigate();
  const defaultValues = { produto_id: null };
  const [produtos, setProdutos] = useState([]);
  const [localId, setLocalId] = useState(1);
  const [selectedGridId, setSelectedGridId] = useState(null);
  const { id } = useParams();
  const hasDocumento = Boolean(id);
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { getDocumento, documento, getLoading, postLoading, postDocumentos } =
    useContext(DocumentosContext);
  const { openDialog, closeDialog } = useDialog();
  const { control, handleSubmit, setValue } = useForm({ defaultValues });
  const selectedProduto = produtos?.find(
    (p) => String(p?.grid_id) === String(selectedGridId)
  );

  useEffect(() => {
    if (Boolean(id)) {
      getDocumento(id);
    }
  }, []);

  useEffect(() => {
    const rows =
      documento?.DocumentoItems?.map((di, index) => ({
        grid_id: index + 1,
        item_id: di?.id,
        documento_id: di?.documento_id,
        produto_id: di?.produto_id,
        descricao: di?.descricao,
        referencia: di?.referencia,
        quantidade: di?.quantidade,
        quantidade_perdida: 0,
        quantidade_retrabalho: 0,
        Estrutura: di?.DocumentoItemEstruturas?.map((die, estruturaIndex) => ({
          grid_id: estruturaIndex + 1,
          selecionado: false,
          id: die?.id,
          componente_id: die?.componente_id,
          quantidade: die?.quantidade,
          perda: die?.perda,
          volume: die?.volume,
          comprimento: die?.comprimento,
          altura: die?.altura,
          largura: die?.largura,
          produtobase_id: die?.produtobase_id,
        })),
        Processo: di?.DocumentoItemProcessos?.map((dip, processoIndex) => ({
          grid_id: processoIndex + 1,
          selecionado: false,
          id: dip?.id,
          operacao: dip?.operacao,
          descricao: dip?.descricao,
          ccusto_id: dip?.ccusto_id,
          texecucao: dip?.texecucao,
          tpreparacao: dip?.tpreparacao,
          apontamento: dip?.apontamento,
          perda: dip?.perda,
          troco: dip?.troco,
          analisa_id: dip?.analisa_id,
          aprova_id: dip?.aprova_id,
        })),
      })) || [];

    setProdutos(rows);
    setLocalId(rows?.length + 1);
    setSelectedGridId(rows?.[0]?.grid_id || null);
  }, [documento]);

  const onAddItem = async ({ produto_id }) => {
    if (!Boolean(produto_id)) {
      return toastWarning('Nenhum produto selecionado');
    }

    if (Boolean(produtos?.length)) {
      return toastWarning('É permitido adicionar somente um produto');
    }

    const produto = await checkInfosProduto({
      filial_id: documento?.filial_id,
      produto_id,
      cadastro_id: documento?.cadastro_id,
      disponivel: 'SIM',
    });

    if (produtos?.some((p) => p?.id === produto_id && !Boolean(p?.grid_id))) {
      return toastWarning('Produto já inserido');
    }

    setProdutos((prev) => [
      ...prev,
      {
        grid_id: localId,
        produto_id,
        descricao: produto?.ProdutoDescricaos?.[0]?.descricao,
        referencia: produto?.referencia || '',
        quantidade_perdida: 0,
        quantidade_retrabalho: 0,
        Estrutura: produto?.Estruturas?.map((e, estruturaIndex) => ({
          grid_id: estruturaIndex + 1,
          selecionado: false,
          componente_id: e?.componente_id,
          quantidade: e?.quantidade,
          perda: e?.perda,
          volume: e?.volume,
          comprimento: e?.comprimento,
          altura: e?.altura,
          largura: e?.largura,
          produtobase_id: e?.produtobase_id,
        })),
        Processo: produto?.Processos?.map((p, processoIndex) => ({
          grid_id: processoIndex + 1,
          selecionado: false,
          operacao: p?.operacao,
          descricao: p?.descricao,
          ccusto_id: p?.ccusto_id,
          texecucao: p?.texecucao,
          tpreparacao: p?.tpreparacao,
          apontamento: p?.apontamento,
          perda: p?.perda,
          troco: p?.troco,
          analisa_id: p?.analisa_id,
          aprova_id: p?.aprova_id,
        })),
      },
    ]);
    setLocalId((prev) => prev + 1);
    setSelectedGridId(localId);
    setValue('produto_id', null);
  };

  const getNextGridId = (rows = []) =>
    Math.max(0, ...rows?.map((row) => Number(row?.grid_id) || 0)) + 1;

  const onDeleteItem = (row) =>
    setProdutos((prev) => {
      const filtered = prev?.filter(
        (p) => String(p?.grid_id) !== String(row?.grid_id)
      );

      if (String(selectedGridId) === String(row?.grid_id)) {
        setSelectedGridId(filtered?.[0]?.grid_id || null);
      }

      return filtered;
    });

  const onDeleteEstrutura = (row) => {
    setProdutos((prev) =>
      prev?.map((produto) =>
        String(produto?.grid_id) === String(selectedGridId)
          ? {
              ...produto,
              Estrutura: produto?.Estrutura?.filter(
                (estrutura) =>
                  String(estrutura?.grid_id) !== String(row?.grid_id)
              ),
            }
          : produto
      )
    );
  };

  const onDeleteProcesso = (row) => {
    setProdutos((prev) =>
      prev?.map((produto) =>
        String(produto?.grid_id) === String(selectedGridId)
          ? {
              ...produto,
              Processo: produto?.Processo?.filter(
                (processo) => String(processo?.grid_id) !== String(row?.grid_id)
              ),
            }
          : produto
      )
    );
  };

  const onEstruturaSelectionChange = (selection) => {
    setProdutos((prev) =>
      prev?.map((produto) =>
        String(produto?.grid_id) === String(selectedGridId)
          ? {
              ...produto,
              Estrutura: produto?.Estrutura?.map((estrutura) => ({
                ...estrutura,
                selecionado: selection?.some(
                  (id) => String(id) === String(estrutura?.grid_id)
                ),
              })),
            }
          : produto
      )
    );
  };

  const onProcessoSelectionChange = (selection) => {
    setProdutos((prev) =>
      prev?.map((produto) =>
        String(produto?.grid_id) === String(selectedGridId)
          ? {
              ...produto,
              Processo: produto?.Processo?.map((processo) => ({
                ...processo,
                selecionado: selection?.some(
                  (id) => String(id) === String(processo?.grid_id)
                ),
              })),
            }
          : produto
      )
    );
  };

  const onSubmitEstrutura = (values) => {
    if (!selectedProduto) {
      return toastWarning('Nenhum item selecionado');
    }

    const componenteId = values?.componente_id || values?.componente;

    if (!Boolean(componenteId)) {
      return toastWarning('Nenhum componente selecionado');
    }

    setProdutos((prev) =>
      prev?.map((produto) => {
        if (String(produto?.grid_id) !== String(selectedGridId)) {
          return produto;
        }

        const estruturas = produto?.Estrutura || [];
        const gridId = values?.grid_id || getNextGridId(estruturas);
        const data = {
          ...values,
          grid_id: gridId,
          selecionado: values?.selecionado ?? true,
          componente_id: componenteId,
        };
        delete data?.componente;
        const exists = estruturas?.some(
          (estrutura) => String(estrutura?.grid_id) === String(gridId)
        );

        return {
          ...produto,
          Estrutura: exists
            ? estruturas?.map((estrutura) =>
                String(estrutura?.grid_id) === String(gridId)
                  ? { ...estrutura, ...data }
                  : estrutura
              )
            : [...estruturas, data],
        };
      })
    );
    closeDialog();
  };

  const onSubmitProcesso = (values) => {
    if (!selectedProduto) {
      return toastWarning('Nenhum item selecionado');
    }

    setProdutos((prev) =>
      prev?.map((produto) => {
        if (String(produto?.grid_id) !== String(selectedGridId)) {
          return produto;
        }

        const processos = produto?.Processo || [];
        const gridId = values?.grid_id || getNextGridId(processos);
        const data = {
          ...values,
          grid_id: gridId,
          selecionado: values?.selecionado ?? true,
          ccusto_id: values?.ccusto_id || null,
        };
        const exists = processos?.some(
          (processo) => String(processo?.grid_id) === String(gridId)
        );

        return {
          ...produto,
          Processo: exists
            ? processos?.map((processo) =>
                String(processo?.grid_id) === String(gridId)
                  ? { ...processo, ...data }
                  : processo
              )
            : [...processos, data],
        };
      })
    );
    closeDialog();
  };

  const EstruturaModal = ({ item }) => {
    const { control, handleSubmit } = useForm({
      defaultValues: {
        grid_id: item?.grid_id || null,
        selecionado: item?.selecionado ?? true,
        id: item?.id,
        componente_id: item?.componente_id || '',
        quantidade: item?.quantidade || '',
        perda: item?.perda || '',
        volume: item?.volume || '',
        comprimento: item?.comprimento || '',
        altura: item?.altura || '',
        largura: item?.largura || '',
        produtobase_id: item?.produtobase_id || null,
      },
    });

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <VirtualDrop
            label="Componente"
            name="componente_id"
            control={control}
            options={drops?.Produto}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="quantidade"
            control={control}
            label="Quantidade Unitária"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask name="perda" control={control} label="Perda" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask name="volume" control={control} label="Volume" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask name="comprimento" control={control} label="Comprimento" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask name="altura" control={control} label="Altura" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask name="largura" control={control} label="Largura" />
        </Grid>
        <Grid item xs={12}>
          <VirtualDrop
            label="Produto Base"
            name="produtobase_id"
            control={control}
            options={drops?.Produto}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button variant="contained" onClick={handleSubmit(onSubmitEstrutura)}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  const ProcessoModal = ({ item }) => {
    const { control, handleSubmit } = useForm({
      defaultValues: {
        grid_id: item?.grid_id || null,
        selecionado: item?.selecionado ?? true,
        id: item?.id,
        operacao: item?.operacao || '',
        descricao: item?.descricao || '',
        ccusto_id: item?.ccusto_id || null,
        tpreparacao: item?.tpreparacao || '',
        texecucao: item?.texecucao || '',
        apontamento: item?.apontamento || null,
        perda: item?.perda || null,
        troco: item?.troco || null,
        analisa_id: item?.analisa_id || null,
        aprova_id: item?.aprova_id || null,
      },
    });

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <InputMask name="operacao" control={control} label="Operação" />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Input name="descricao" control={control} label="Descrição" />
        </Grid>
        <Grid item xs={12}>
          <VirtualDrop
            label="Centro de Custo"
            name="ccusto_id"
            control={control}
            options={drops?.Ccusto}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="tpreparacao"
            control={control}
            label="Tempo de Preparação Unitário (Centesimal)"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="texecucao"
            control={control}
            label="Tempo de Execução Unitário (Centesimal)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="apontamento"
            control={control}
            label="Apontamento"
            options={dropBoolean}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="perda"
            control={control}
            label="Perda"
            options={dropBoolean}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="troco"
            control={control}
            label="Troco"
            options={dropBoolean}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            name="analisa_id"
            control={control}
            label="Usuário Analista"
            options={drops?.Users}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            name="aprova_id"
            control={control}
            label="Usuário Aprovador"
            options={drops?.Users}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button variant="contained" onClick={handleSubmit(onSubmitProcesso)}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  const openEstruturaModal = (item) => {
    if (!selectedProduto) {
      return toastWarning('Nenhum item selecionado');
    }

    openDialog(
      <EstruturaModal item={item} />,
      item?.grid_id ? 'Editar Estrutura' : 'Adicionar Estrutura'
    );
  };

  const openProcessoModal = (item) => {
    if (!selectedProduto) {
      return toastWarning('Nenhum item selecionado');
    }

    openDialog(
      <ProcessoModal item={item} />,
      item?.grid_id ? 'Editar Processo' : 'Adicionar Processo'
    );
  };

  const onCellEditCommit = (e) => {
    if (!['quantidade_perdida', 'quantidade_retrabalho'].includes(e?.field)) {
      return;
    }

    setProdutos((prev) =>
      prev?.map((produto) => {
        if (String(produto?.grid_id) !== String(e?.id)) {
          return produto;
        }

        const outroCampo =
          e?.field === 'quantidade_perdida'
            ? 'quantidade_retrabalho'
            : 'quantidade_perdida';
        const quantidadeItem = Number(produto?.quantidade);
        const saldo = Math.max(
          0,
          quantidadeItem - Number(produto?.[outroCampo] || 0)
        );
        const quantidade = Number(e?.value);

        return {
          ...produto,
          [e?.field]: Number.isFinite(quantidade)
            ? Math.min(Math.max(0, quantidade), saldo)
            : 0,
        };
      })
    );
  };

  const getEstruturasSelecionadas = (estruturas = []) =>
    estruturas
      ?.filter((estrutura) => estrutura?.selecionado)
      ?.map((estrutura) => ({
        componente_id: estrutura?.componente_id,
        quantidade: estrutura?.quantidade,
        perda: estrutura?.perda,
        volume: estrutura?.volume,
        comprimento: estrutura?.comprimento,
        altura: estrutura?.altura,
        largura: estrutura?.largura,
        produtobase_id: estrutura?.produtobase_id,
      }));

  const getProcessosSelecionados = (processos = []) =>
    processos
      ?.filter((processo) => processo?.selecionado)
      ?.map((processo) => ({
        ccusto_id: processo?.ccusto_id,
        descricao: processo?.descricao,
        operacao: processo?.operacao,
        texecucao: processo?.texecucao,
        tpreparacao: processo?.tpreparacao,
        apontamento: processo?.apontamento,
        perda: processo?.perda,
        troco: processo?.troco,
        analisa_id: processo?.analisa_id,
        aprova_id: processo?.aprova_id,
      }));

  const onSubmit = () => {
    const data = [];
    const itensPerda = produtos?.filter(
      (produto) => Number(produto?.quantidade_perdida || 0) > 0
    );
    const itensRetrabalho = produtos?.filter(
      (produto) => Number(produto?.quantidade_retrabalho || 0) > 0
    );

    const of_perda = {
      Documento: {
        especie_id: 17,
        natureza_operacao_id: 17,
        users_id: user?.id,
        filial_id: documento?.filial_id || user?.filial_id,
        cadastro_id: documento?.cadastro_id || user?.entidade_id,
        celocal_id: documento?.celocal_id,
      },
      Itens: itensPerda?.map((m, i) => ({
        sequencia: i + 1,
        produto_id: m?.produto_id,
        quantidade: m?.quantidade_perdida,
        descricao: m?.descricao,
        referencia: m?.referencia,
        ...(Boolean(m?.item_id) && {
          Origens: [{ documento_item_org_id: m?.item_id }],
        }),
        Estruturas: m?.Estrutura?.map((estrutura) => ({
          componente_id: estrutura?.componente_id,
          quantidade: estrutura?.quantidade,
          perda: estrutura?.perda,
          volume: estrutura?.volume,
          comprimento: estrutura?.comprimento,
          altura: estrutura?.altura,
          largura: estrutura?.largura,
          produtobase_id: estrutura?.produtobase_id,
        })),
        Processos: m?.Processo?.map((processo) => ({
          ccusto_id: processo?.ccusto_id,
          descricao: processo?.descricao,
          operacao: processo?.operacao,
          texecucao: processo?.texecucao,
          tpreparacao: processo?.tpreparacao,
          apontamento: processo?.apontamento,
          perda: processo?.perda,
          troco: processo?.troco,
          analisa_id: processo?.analisa_id,
          aprova_id: processo?.aprova_id,
        })),
        Qualidades: [
          {
            sequencia: 1,
            qualidade_indicador_id: 1,
            Respostas: [
              {
                qualidade_indicador_resp_id: 4,
                datahora: moment().format('YYYY-MM-DD HH:mm:ss'),
                resultado: m?.quantidade_perdida,
              },
            ],
          },
        ],
      })),
    };

    if (itensPerda?.length) {
      data.push(of_perda);
    }

    const of_retrabalho = {
      Documento: {
        especie_id: 17,
        natureza_operacao_id: 17,
        users_id: user?.id,
        filial_id: documento?.filial_id || user?.filial_id,
        cadastro_id: documento?.cadastro_id || user?.entidade_id,
        celocal_id: documento?.celocal_id,
      },
      Itens: itensRetrabalho?.map((m, i) => ({
        sequencia: i + 1,
        produto_id: m?.produto_id,
        quantidade: m?.quantidade_retrabalho,
        descricao: m?.descricao,
        referencia: m?.referencia,
        ...(Boolean(m?.item_id) && {
          Origens: [{ documento_item_org_id: m?.item_id }],
        }),
        ...(Boolean(getEstruturasSelecionadas(m?.Estrutura)?.length) && {
          Estruturas: getEstruturasSelecionadas(m?.Estrutura),
        }),
        ...(Boolean(getProcessosSelecionados(m?.Processo)?.length) && {
          Processos: getProcessosSelecionados(m?.Processo),
        }),
        Qualidades: [
          {
            sequencia: 1,
            qualidade_indicador_id: 1,
            Respostas: [
              {
                qualidade_indicador_resp_id: 3,
                datahora: moment().format('YYYY-MM-DD HH:mm:ss'),
                resultado: m?.quantidade_retrabalho,
              },
            ],
          },
        ],
      })),
    };

    if (itensRetrabalho?.length) {
      data.push(of_retrabalho);
    }

    if (!hasDocumento || documento?.situacao === 'CONCLUIDO') {
      const rnd_perda = {
        Documento: {
          especie_id: 55,
          natureza_operacao_id: 28,
          users_id: user?.id,
          filial_id: documento?.filial_id || user?.filial_id,
          cadastro_id: documento?.cadastro_id || user?.entidade_id,
          celocal_id: documento?.celocal_id,
        },
        Itens: itensPerda?.map((m, i) => ({
          sequencia: i + 1,
          produto_id: m?.produto_id,
          quantidade: m?.quantidade_perdida,
          descricao: m?.descricao,
          referencia: m?.referencia,
          ...(Boolean(m?.item_id) && {
            Origens: [{ documento_item_org_id: m?.item_id }],
          }),
        })),
      };
      if (itensPerda?.length) {
        data.push(rnd_perda);
      }

      const rnd_retrabalho = {
        Documento: {
          especie_id: 55,
          natureza_operacao_id: 28,
          users_id: user?.id,
          filial_id: documento?.filial_id || user?.filial_id,
          cadastro_id: documento?.cadastro_id || user?.entidade_id,
          celocal_id: documento?.celocal_id,
        },
        Itens: itensRetrabalho?.map((m, i) => ({
          sequencia: i + 1,
          produto_id: m?.produto_id,
          quantidade: m?.quantidade_retrabalho,
          descricao: m?.descricao,
          referencia: m?.referencia,
          ...(Boolean(m?.item_id) && {
            Origens: [{ documento_item_org_id: m?.item_id }],
          }),
        })),
      };
      if (itensRetrabalho?.length) {
        data.push(rnd_retrabalho);
      }
    }

    postDocumentos({ data, cb: () => navigate(-1) });
  };

  const itemColumns = [
    {
      field: 'descricao',
      headerName: 'Produto',
      minWidth: 300,
      flex: 1,
      sortable: false,
    },
    {
      field: 'referencia',
      headerName: 'Referência',
      width: 150,
      sortable: false,
    },
    {
      field: 'quantidade',
      headerName: 'Quantidade',
      type: 'number',
      width: 200,
      sortable: false,
    },
    {
      field: 'quantidade_perdida',
      headerName: 'Quantidade Perdida',
      type: 'number',
      width: 200,
      editable: true,
      sortable: false,
    },
    {
      field: 'quantidade_retrabalho',
      headerName: 'Quantidade de Retrabalho',
      type: 'number',
      width: 240,
      editable: true,
      sortable: false,
    },
    ...(!hasDocumento
      ? [
          {
            field: 'actions',
            headerName: 'Ações',
            type: 'actions',
            width: 100,
            getActions: ({ row }) => [
              <GridActionsCellItem
                icon={<Icon>delete</Icon>}
                label="Excluir"
                onClick={() => onDeleteItem(row)}
              />,
            ],
          },
        ]
      : []),
  ];

  if (getLoading) {
    return <Loader />;
  }

  return (
    <Container>
      <Header titulo="Rejeição" />
      <Grid container spacing={2}>
        {!hasDocumento && (
          <Grid item xs={12} display="flex" alignItems="center" gap={2}>
            <Box flex={1}>
              <VirtualDrop
                label="Produto"
                name="produto_id"
                control={control}
                options={drops?.Produto}
              />
            </Box>
            <Button variant="outlined" onClick={handleSubmit(onAddItem)}>
              Adicionar
            </Button>
          </Grid>
        )}
        <Grid item xs={12} textAlign="center">
          <Typography variant="h6">Itens</Typography>
          <Typography variant="caption" color="text.secondary">
            Selecione o item para definir estrutura e processo
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={produtos}
            columns={itemColumns}
            getRowId={(row) => row?.grid_id}
            selectionModel={selectedGridId !== null ? [selectedGridId] : []}
            onSelectionModelChange={(selection) =>
              setSelectedGridId(selection?.[0] || null)
            }
            onRowClick={({ row }) => setSelectedGridId(row?.grid_id)}
            hideFooter
            autoHeight
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            localeText={localeText}
            onCellEditCommit={onCellEditCommit}
          />
        </Grid>
        {Number(selectedProduto?.quantidade_retrabalho || 0) > 0 && (
          <>
            <Grid item xs={12} textAlign="center">
              <Typography variant="h6">Estrutura</Typography>
              {selectedProduto && (
                <Typography variant="caption" color="text.secondary">
                  {selectedProduto?.descricao}
                </Typography>
              )}
              <Typography
                display="block"
                variant="caption"
                color="text.secondary"
              >
                Somente as estruturas selecionadas serão enviadas.
              </Typography>
            </Grid>
            <Grid item xs={12} textAlign="center">
              <Button
                variant="outlined"
                disabled={!selectedProduto}
                onClick={() => openEstruturaModal()}
              >
                Adicionar Estrutura
              </Button>
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                rows={selectedProduto?.Estrutura || []}
                columns={[
                  {
                    field: 'componente_id',
                    headerName: 'Componente',
                    minWidth: 300,
                    flex: 1,
                    sortable: false,
                    valueFormatter: ({ value }) =>
                      drops?.Produto?.find(
                        (f) => String(f?.value) === String(value)
                      )?.label,
                  },
                  {
                    field: 'quantidade',
                    headerName: 'Quantidade Unitária',
                    type: 'number',
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'quantidade_total',
                    headerName: 'Quantidade Total',
                    type: 'number',
                    width: 200,
                    sortable: false,
                    valueGetter: ({ row }) =>
                      Number(row?.quantidade || 0) *
                      Number(selectedProduto?.quantidade_retrabalho || 0),
                  },
                  {
                    field: 'actions',
                    headerName: 'Ações',
                    type: 'actions',
                    width: 120,
                    getActions: ({ row }) => [
                      <GridActionsCellItem
                        icon={<Icon>edit</Icon>}
                        label="Editar"
                        onClick={() => openEstruturaModal(row)}
                      />,
                      <GridActionsCellItem
                        icon={<Icon>delete</Icon>}
                        label="Excluir"
                        onClick={() => onDeleteEstrutura(row)}
                      />,
                    ],
                  },
                ]}
                getRowId={(row) => row?.grid_id}
                checkboxSelection
                selectionModel={
                  selectedProduto?.Estrutura?.filter(
                    (row) => row?.selecionado
                  )?.map((row) => row?.grid_id) || []
                }
                onSelectionModelChange={onEstruturaSelectionChange}
                hideFooter
                autoHeight
                density="compact"
                disableColumnMenu
                showCellRightBorder
                showColumnRightBorder
                localeText={localeText}
              />
            </Grid>
            <Grid item xs={12} textAlign="center">
              <Typography variant="h6">Processos</Typography>
              {selectedProduto && (
                <Typography variant="caption" color="text.secondary">
                  {selectedProduto?.descricao}
                </Typography>
              )}
              <Typography
                display="block"
                variant="caption"
                color="text.secondary"
              >
                Somente os processos selecionados serão enviados.
              </Typography>
            </Grid>
            <Grid item xs={12} textAlign="center">
              <Button
                variant="outlined"
                disabled={!selectedProduto}
                onClick={() => openProcessoModal()}
              >
                Adicionar Processo
              </Button>
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                rows={selectedProduto?.Processo || []}
                columns={[
                  {
                    field: 'operacao',
                    headerName: 'Operação',
                    width: 150,
                    sortable: false,
                  },
                  {
                    field: 'descricao',
                    headerName: 'Descrição',
                    minWidth: 300,
                    flex: 1,
                    sortable: false,
                  },
                  {
                    field: 'ccusto_id',
                    headerName: 'Centro de Custo',
                    minWidth: 220,
                    flex: 1,
                    sortable: false,
                    valueFormatter: ({ value }) =>
                      drops?.Ccusto?.find(
                        (f) => String(f?.value) === String(value)
                      )?.label,
                  },
                  {
                    field: 'tpreparacao',
                    headerName: 'Tempo de Preparação Unitário',
                    type: 'number',
                    width: 300,
                    sortable: false,
                  },
                  {
                    field: 'texecucao',
                    headerName: 'Tempo de Execução Unitário',
                    type: 'number',
                    width: 300,
                    sortable: false,
                  },
                  {
                    field: 'actions',
                    headerName: 'Ações',
                    type: 'actions',
                    width: 120,
                    getActions: ({ row }) => [
                      <GridActionsCellItem
                        icon={<Icon>edit</Icon>}
                        label="Editar"
                        onClick={() => openProcessoModal(row)}
                      />,
                      <GridActionsCellItem
                        icon={<Icon>delete</Icon>}
                        label="Excluir"
                        onClick={() => onDeleteProcesso(row)}
                      />,
                    ],
                  },
                ]}
                getRowId={(row) => row?.grid_id}
                checkboxSelection
                selectionModel={
                  selectedProduto?.Processo?.filter(
                    (row) => row?.selecionado
                  )?.map((row) => row?.grid_id) || []
                }
                onSelectionModelChange={onProcessoSelectionChange}
                hideFooter
                autoHeight
                density="compact"
                disableColumnMenu
                showCellRightBorder
                showColumnRightBorder
                localeText={localeText}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} textAlign="center">
          <Button
            variant="contained"
            onClick={onSubmit}
            loading={postLoading}
            disabled={
              !produtos?.some(
                (produto) =>
                  Number(produto?.quantidade_perdida || 0) > 0 ||
                  Number(produto?.quantidade_retrabalho || 0) > 0
              )
            }
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Rejeição;
